import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface Branch {
    branch_id: number;
    branch_name: string;
    branch_registration: string;
    branch_address1: string;
    branch_address2: string;
    branch_city: string;
    branch_state: string;
    branch_zip: string;
    branch_phone: string;
    branch_fax: string;
    branch_email: string;
    branch_crew_hourly_rate: any;
    branch_all_ca_zips?: number;
    zip_count?: number;
}

interface Territory { branch_zip_id: number; branch_zip_zip: number; branch_zip_city: string; branch_zip_county: string; }

const API = environment.api + 'branches-settings';

/**
 * Settings > Company & Branding > Branches / ZIPs (mockup settings "Branches / ZIPs"; twin-ci
 * branches_list / add / edit). Mockup look, every twin-ci field kept (agreed 2026-10-09): the PDF
 * headers print the branch address, phone, fax and registration, and Intake picks a job's branch
 * from the ZIP list or the one branch covering all CA ZIP codes.
 */
@Component({
    selector: 'app-branches',
    templateUrl: './branches.component.html',
    styleUrls: ['./branches.component.scss']
})
export class BranchesComponent implements OnInit {
    branches: Branch[] = [];
    allCaHolder: { branch_id: number, branch_name: string } = null;
    states: string[] = [];
    loaded = false;
    search = '';
    message = '';
    error = '';

    // dialog
    editing: Branch = null;          // branch_id 0 = new
    territories: Territory[] = [];
    picked: { [id: number]: boolean } = {};
    newZips = '';
    zipResult: { added: string[], skipped: { zip: string, reason: string }[] } = null;
    saving = false;
    formError = '';
    fieldErrors: { [field: string]: string } = {};
    dialogMessage = '';
    deleting: Branch = null;
    deletingZips = false;

    constructor(private http: HttpClient) { }

    ngOnInit() {
        this.http.get<{ id: string }[]>('assets/states.json').subscribe(list => this.states = list.map(s => s.id));
        this.load();
    }

    private text(err: any, fallback: string) {
        return typeof err === 'string' ? err : fallback;
    }

    private load() {
        this.http.get<any>(API).subscribe(
            res => {
                this.branches = res.data || [];
                this.allCaHolder = res.all_ca_holder;
                this.loaded = true;
            },
            err => this.error = this.text(err, 'The branches could not be loaded.')
        );
    }

    get shown(): Branch[] {
        const q = this.search.trim().toLowerCase();
        return !q ? this.branches : this.branches.filter(b =>
            [b.branch_name, this.address(b), b.branch_phone, b.branch_email].join(' ').toLowerCase().indexOf(q) !== -1);
    }

    address(b: Branch) {
        const street = [b.branch_address1, b.branch_address2].filter(x => x).join(' ');
        const place = [b.branch_city, [b.branch_state, b.branch_zip].filter(x => x).join(' ')].filter(x => x).join(', ');
        return [street, place].filter(x => x).join(', ');
    }

    add() {
        this.open({
            branch_id: 0, branch_name: '', branch_registration: '', branch_address1: '', branch_address2: '',
            branch_city: '', branch_state: 'CA', branch_zip: '', branch_phone: '', branch_fax: '', branch_email: '',
            branch_crew_hourly_rate: ''
        });
    }

    edit(b: Branch) {
        this.open(Object.assign({}, b));
        this.loadBranch(b.branch_id);
    }

    private open(b: Branch) {
        this.editing = b;
        this.territories = [];
        this.picked = {};
        this.newZips = '';
        this.zipResult = null;
        this.formError = this.dialogMessage = '';
        this.fieldErrors = {};
    }

    close() {
        this.editing = null;
    }

    /** The branch with its territories (and who holds all CA ZIP codes). */
    private loadBranch(id: number) {
        this.http.get<any>(API + '/' + id).subscribe(
            res => {
                if (!this.editing || this.editing.branch_id !== id) {
                    return;
                }
                this.editing.branch_all_ca_zips = res.branch_all_ca_zips;
                this.territories = res.zips || [];
                this.allCaHolder = res.all_ca_holder;
                this.picked = {};
            },
            err => this.formError = this.text(err, 'The territories could not be loaded.')
        );
    }

    private validate() {
        const b = this.editing;
        const e: { [field: string]: string } = {};
        const required = {
            branch_name: 'Name', branch_registration: 'Registration #', branch_address1: 'Street Address',
            branch_city: 'City', branch_state: 'State', branch_zip: 'ZIP Code', branch_phone: 'Phone', branch_email: 'Email',
        };
        Object.keys(required).forEach(k => { if (!String(b[k] || '').trim()) { e[k] = required[k] + ' is required.'; } });
        if (!e.branch_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(b.branch_email).trim())) {
            e.branch_email = 'Enter a valid email address.';
        }
        const rate = b.branch_crew_hourly_rate;
        if (rate !== '' && rate !== null && !(isFinite(Number(rate)) && Number(rate) >= 0)) {
            e.branch_crew_hourly_rate = 'Enter an amount, 0 or more.';
        }
        this.fieldErrors = e;
        return !Object.keys(e).length;
    }

    save() {
        this.formError = this.dialogMessage = '';
        if (!this.validate()) {
            this.formError = 'Check the highlighted fields.';
            return;
        }
        const b = this.editing;
        const body = {};
        ['branch_name', 'branch_registration', 'branch_address1', 'branch_address2', 'branch_city', 'branch_state',
            'branch_zip', 'branch_phone', 'branch_fax', 'branch_email', 'branch_crew_hourly_rate']
            .forEach(k => body[k] = b[k] === null || b[k] === undefined ? '' : String(b[k]).trim());
        this.saving = true;
        const request = b.branch_id ? this.http.put<any>(API + '/' + b.branch_id, body) : this.http.post<any>(API, body);
        request.subscribe(
            res => {
                this.saving = false;
                this.load();
                if (b.branch_id) {
                    this.message = 'Branch Edited';
                    this.close();
                    return;
                }
                // twin-ci: "New Branch Added - Now Add any Territories" - stay in the dialog on the new branch.
                b.branch_id = res.id;
                this.dialogMessage = 'New Branch Added - now add any territories.';
                this.loadBranch(res.id);
                if (this.newZips.trim()) {
                    this.addZips();
                }
            },
            err => {
                this.saving = false;
                this.formError = this.text(err, 'The branch could not be saved.');
            }
        );
    }

    addZips() {
        if (!this.editing.branch_id || !this.newZips.trim()) {
            return;
        }
        this.saving = true;
        this.zipResult = null;
        this.http.post<any>(API + '/' + this.editing.branch_id + '/zips', { zips: this.newZips }).subscribe(
            res => {
                this.saving = false;
                this.zipResult = res;
                this.newZips = (res.skipped || []).map(s => s.zip).join(', ');
                this.loadBranch(this.editing.branch_id);
                this.load();
            },
            err => {
                this.saving = false;
                this.formError = this.text(err, 'The ZIP codes could not be added.');
            }
        );
    }

    get pickedIds(): number[] {
        return Object.keys(this.picked).filter(k => this.picked[k]).map(Number);
    }

    pickAll(on: boolean) {
        this.picked = {};
        if (on) {
            this.territories.forEach(t => this.picked[t.branch_zip_id] = true);
        }
    }

    deleteZips() {
        this.saving = true;
        this.http.post<any>(API + '/' + this.editing.branch_id + '/zips/delete', { ids: this.pickedIds }).subscribe(
            res => {
                this.saving = false;
                this.deletingZips = false;
                this.dialogMessage = res.message;
                this.loadBranch(this.editing.branch_id);
                this.load();
            },
            err => {
                this.saving = false;
                this.deletingZips = false;
                this.formError = this.text(err, 'The territories could not be deleted.');
            }
        );
    }

    get otherHoldsAllCa() {
        return this.allCaHolder && this.editing && this.allCaHolder.branch_id !== this.editing.branch_id;
    }

    setAllCa(on: boolean) {
        this.saving = true;
        this.formError = '';
        this.http.put<any>(API + '/' + this.editing.branch_id + '/all-ca', { on }).subscribe(
            res => {
                this.saving = false;
                this.dialogMessage = res.message;
                this.loadBranch(this.editing.branch_id);
                this.load();
            },
            err => {
                this.saving = false;
                this.formError = this.text(err, 'The setting could not be saved.');
                this.loadBranch(this.editing.branch_id);
            }
        );
    }

    confirmDelete(b: Branch) {
        this.deleting = b;
    }

    remove() {
        this.saving = true;
        this.http.delete<any>(API + '/' + this.deleting.branch_id).subscribe(
            res => {
                this.saving = false;
                this.deleting = null;
                this.message = res.message || 'Branch Deleted';
                this.load();
            },
            err => {
                this.saving = false;
                this.deleting = null;
                this.error = this.text(err, 'The branch could not be deleted.');
            }
        );
    }
}
